import FilterTags from './filter-tags';
import analytics from '../../analytics';
import CarSearchFreeText from './car-search-free-text';
import {
  convertUrlSearchParams,
  elementIsCenterOfScreen,
  formatAjaxBody,
  formatSelectedFiltersToDataLayer,
  pushFiltersToDataLayer,
  toggleSearchActive,
  formatSelectedFiltersData,
  isCheckboxOrRadioFilter,
  isTextInput,
  isSelectElement,
  handleCheckboxAndRadio,
  handleTextInput,
  handleSelect,
  compareArrays,
} from '../2023/car-search-helper';
import ModalForm from '../../forms/2023/modal-form';
import globals from '../../globals';


/**
 * Defines sessionStorage key where last results will be
 * saved when user clicks car card
 *
 * @type {string}
 */
const SESSION_STORAGE_KEY = 'car-search-last-results';

/**
 * Last paging value sessionStorage key
 * where last paging value is saved.
 *
 * @type {string}
 */
const LAST_PAGING_SESSION_STORAGE_KEY = 'car-search-last-paging-value';

const MAIN_FILTER_TAB_IDS = {
  store: 'tab-store-cars',
  orderable: 'tab-orderable-cars'
};

const ORDERABLE_FILTER_SUFFIX = '--orderable';

/**
 * Adds functionality to car search
 */
class CarSearch {

  /**
   * Car search instance itself
   *
   * @type {instance|boolean}
   * @static
   */
  static instance = false;

  /**
   * Car search container
   * @type {Element}
   * @private
  */
  element;

  /**
   * If results loaded from sessionStorage this will be true
   * to indicate popstate event not to fire search function
   *
   * @type {boolean}
   * @private
   */
  #sessionStorageLoaded = false;

  /**
   * Element querySelector search strings
   * Used when constructed, loop and fill elementPartials
   *
   * @type {object}
   * @private
   */
  #elementPartialsQueryStrings = {
    loadMore: '#car-search-load-more',
    changeOrder: '.car-search-main__results-order-dropdown',
    resultsCount: '.car-search-main__results-count-container#results-count',
    carsGrid: '.container.cars',
    noMoreResults: '#car-search-no-more-results',
    loadMoreSpinner: '.car-search-main__loader-holder',
    carSearchTitle: '#main-heading',
    carSearchSeo: '.seo-text-element',
    evFilterGroup: '.ev-group',
    blurbContainer: '.car-search-blurb-container',
    searchWatcherButton: '.car-search-main__search-watcher-button',
  }

  /**
   * Maximum pages load with infinite scroll
   * until the "load more" button will set visible
   *
   * @type {number}
   * @private
   */
  #maxInfiniteLoadPages = 2;

  /**
   * Element querySelectorAll search strings
   * Used when constructed, loop and fill elementPartials
   *
   * @type {object}
   * @private
   */
  #multipleElementPartialsQueryStrings = {
    carCountElements: '[data-car-count]',
    filterCountElements: '[data-filter-count]',
  }

  /**
   * Different kind of element partials inside
   * Car Search, such as "Load more" -buttons etc.
   *
   * @type {object}
   * @private
   */
  #elementPartials = {}

  /**
   * Inputs contains all inputs available in car search
   *
   * @type {HTMLElement[]}
   * @private
   */
  #inputs = [];

  /**
   * Handle modal element definition
   *
   * @type {HTMLElement}
   * @private
   */
  #handleModal = document.querySelector('.handle-modal');

  /**
   * Search results container
   *
   * @type {HTMLElement}
   * @private
   */
  #resultsContainer = document.querySelector('.car-search-results-container');

  /**
   * SEO text container
   *
   * @type {HTMLElement}
   * @private
   */
  #seoTextContainer = document.querySelector('.seo-text-container');

  /**
   * Tax value inputs
   *
   * @type {object}
   * @private
   */
  #taxValueInputs = {
    limited_benefit_min: document.querySelector('input[name="limited_benefit_min"]'),
    limited_benefit_max: document.querySelector('input[name="limited_benefit_max"]'),
    unlimited_benefit_min: document.querySelector('input[name="unlimited_benefit_min"]'),
    unlimited_benefit_max: document.querySelector('input[name="unlimited_benefit_max"]'),
  };

  /**
   * Different types of cards data
   *
   * @type {object}
   * @private
   */
  #cards = {
    basicCard: JSON.parse(document.getElementById('basic-card-json').innerHTML),
    taxValueCard: JSON.parse(document.getElementById('tax-value-card-json').innerHTML),
    modelCard: JSON.parse(document.getElementById('model-card-json').innerHTML),
    hedinDriveModelCard: JSON.parse(document.getElementById('hedin-drive-model-card-json').innerHTML),
    liftCard: JSON.parse(document.getElementById('lift-card-json').innerHTML)
  }

  /**
   * Car search SEO text template
   *
   * @type {string}
   * @private
   */
  #carSearchSeoTemplate = document.getElementById('car-search-seo').innerHTML;
  #blurbTemplate = document.getElementById('car-search-blurb').innerHTML;
  #contentPartial = document.getElementById('car-search-content-partial').innerHTML;

  /**
   * Last brand to search. This is used when searching brands
   * If brand is different than last search, get new models
   *
   * @type {string[]}
   * @private
   */
  #lastBrandToSearch = [];

  /**
   * Current brand to force in ajax-search
   *
   * @type {string}
   * @private
   */
  #currentBrand = window.currentBrand;

  /**
   * Currently visible car cards length
   */
  #visibleCarsCount = 0;

  /**
   * Current cars count. Used when comparing cars count
   * and if we need to load more or not.
   *
   * @type {number}
   * @private
   */
  #currentCarsCount = 0;

  /**
   * Current page for loadMore
   *
   * Note:
   * - Internally index start from zero,
   * - For a user index start from one, show it is shown as plus one for end user.
   * - Backend index start from zero.
   *
   * @type {number}
   * @private
   */
  #currentPage = 0;

  /**
   * Last loader state saved here
   * When scrolling and e.g. loader position is fixed
   * save fixed here so we do not have to set same values
   * to loader element
   *
   * @type {string} - Available values: top, bottom, fixed
   */
  #lastLoaderState = 'top';

  /**
   * Store first and last cards elements here
   * This will avoid querySelectors inside scroll -event
   *
   * @type {object}
   * @private
   */
  #firstAndLastCardElements = {
    first: null,
    last: null
  }

  /**
   * Defines if search app is currently loading new cars
   *
   * @type {boolean}
   * @private
   */
  #isLoading = false;

  /**
   * Defines if orderable tab is active
   *
   * @type {boolean}
   * @private
   */
  #isOrderableTabActive = false;

  /**
   * Abort controller to handle ajax-requests aborts
   *
   * @type {AbortController|boolean} - Default is false
   */
  abortController = false

  /**
   * Constructor. Starts everything up if accordion elements are found
   * @param {HTMLElement} element element to bind accordion item functionality
   * @returns {CarSearch} CarSearch singleton instance
   */
  constructor(element) {
    if (CarSearch.instance) {
      // Return singleton reference if it exists already
      return CarSearch.instance;
    }

    // Run filter tags init in case it is not constructed yet
    FilterTags.init();

    // Store singleton reference for future instances.
    CarSearch.instance = this;
    this.element = element;

    // Read current page index from an input field rendered in back end. Index star from one but backend it start from zero.
    //In the URL we reat index way it start from 1 and to backend request we change index to zero. Internally in car-search.js we use index start from zero.
    this.#currentPage = parseInt(element.querySelector('input[name="page"]').value) - 1;

    // Initialize free text search
    new CarSearchFreeText(CarSearch.instance);

    // Fill elementPartials member
    for (const [elementName, queryString] of Object.entries(this.#elementPartialsQueryStrings)) {
      this.#elementPartials[elementName] = this.element.querySelector(queryString);
    }

    // Chademo electric plug type
    this.#elementPartials.chademo = this.element.querySelector('#CHAdeMO').closest('.radio-element');

    // Fill elementPartials member
    for (const [elementName, queryString] of Object.entries(this.#multipleElementPartialsQueryStrings)) {
      this.#elementPartials[elementName] = this.element.querySelectorAll(queryString);
    }

    // Get current cars count, parse window.currentPaging
    // window.currentPaging is printed straight to the dom
    // @see https://laakkonen.stage.crasman.fi/admin/manage/scriptlets/scriptleteditor/html/code/show/JSltD3ssSRScHAwmZMEOag <- mustache template
    // @see https://laakkonen.stage.crasman.fi/admin/manage/scriptlets/scriptleteditor/site/stagemodule/show/r78Cu8iBQDGQGBDb-P0FnA at `get data() {`
    this.#currentCarsCount = +JSON.parse(window.currentPaging).totalResults;

    this.#visibleCarsCount = this.#elementPartials.carsGrid.querySelectorAll('.card--basic-card').length;

    // Set the correct inputs based on active tab.
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('category[]') === 'tilattavat-autot') {
      this.#isOrderableTabActive = true;
    }
    const activeTabId = this.#isOrderableTabActive ? MAIN_FILTER_TAB_IDS.orderable : MAIN_FILTER_TAB_IDS.store;
    const activeMainTabPanel = this.#handleModal?.querySelector(`#${activeTabId}`);
    const panelInputs = activeMainTabPanel?.querySelectorAll('.car-search-effective, .handle-modal select') ?? [];
    const orderSelectInputs = document.querySelectorAll('#order-select-container .car-search-effective');
    this.#inputs = [...panelInputs, ...orderSelectInputs];

    if (this.#isOrderableTabActive) {
      this.#elementPartials.searchWatcherButton?.classList.add('hidden');
    } else {
      this.#elementPartials.searchWatcherButton?.classList.remove('hidden');
    }

    // Bind element events
    this.#bindEvents();
    // If URL already contains tilattavat-autot on page load, activate orderable tab state
    this.#initOrderableTabFromUrl();

    const selectedFilters = formatSelectedFiltersData(Array.from(this.#inputs));
    this.updateFilterCountElements(selectedFilters);

    // Update filters
    this.#updateFilterTags(selectedFilters);

    // Update last brand if exists in selectedFilters
    const hasBrandSelection = selectedFilters.filter(f => f.name === 'brands[]').map( f => f.value );
    if (hasBrandSelection.length > 0) {
      this.#lastBrandToSearch = hasBrandSelection;
    }

    // Handle sessionStorage results if exists
    this.#handleResultsFromSessionStorage();

    this.#observeLoadMoreButton();

    // Save initial history state
    history.pushState(selectedFilters, '');

    this.handleModelDropdown();
    this.#handleNoMoreResultsLeft();

    // Set first and last cards position info
    this.#setFirstAndLastCardsPositionInfo();
    // Set loader initial top value
    this.#setLoaderInitialTopValue();

    // Change button visible after scroll
    this.#changeButtonVisibleAfterScroll();

    this.#handlePropulsionChange();
    this.#handleTaxValueVisibility();

    // On mobile, open all filters automatically when URL has hd=1
    this.#handleHedinDriveMobileAutoOpen();
  }

  /**
   * On mobile devices, if the URL contains the GET-parameter hd=1,
   * automatically open the "all filters" modal.
   *
   * @private
   */
  #handleHedinDriveMobileAutoOpen() {
    if (globals.isMobile) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('hd') !== '1') {
      return;
    }

    document.getElementById('car-search-allfilters')?.click();
  }

  /**
   * Search cars and update filter tags.
   * @param {Boolean} updateTags - Update filter tags. Default is true.
   * @param {object}  options    - Options object
   * @param {boolean} options.loadMore - ????
   * @param {boolean} options.scrollToTop - Smooth scrolls to top of the page
   * @param {boolean} options.doNotSaveHistory - Pass true to not save history state and update URL-address
   * @param {boolean} doNotHandleModelDropdown - If set true, function will not update models dropdown
   *                                             e.g. in case of model dropdown is handled earlier (free text search)
   */
  async searchCars(updateTags = true, options = {}) {
    this.#seoTextContainer.hidden = false;
    this.#resultsContainer.hidden = false;

    this.toggleLoadingAnimation = true;

    // Force calculate position for spinner
    this.#calculateLoadingSpinnerPosition(true);

    // Abort possible last request
    this.#abortSearch();
    this.abortController = new AbortController();

    // If load more option does not exists
    // reset current page, because GET-parameters
    // has changed.
    if (!options.loadMore) {
      this.#currentPage = 0;
    }

    // Handle models dropdown
    if (!options.doNotHandleModelDropdown) {
      // This has to be waited for filter to be updated correctly. Models are fetched from server.
      const activeModelsFilterName = this.#getMainTabFilterName('models[]');
      const activeModelsDropdown = window.multiselects[activeModelsFilterName];
      await this.handleModelDropdown(false, activeModelsDropdown?.getValue());
    }

    const activeBrandsFilterName = this.#getMainTabFilterName('brands[]');
    this.#lastBrandToSearch = window.multiselects[activeBrandsFilterName]?.getValue?.() || [];
    const filters = formatSelectedFiltersData(Array.from(this.#inputs));
    this.updateFilterCountElements(filters);

    const activeCategoryFilterName = this.#getMainTabFilterName('category[]');

    if (this.#isOrderableTabActive) {
      const hasOrderableCategory = filters.some((filter) => this.#normalizeOutgoingFilterName(filter.name) === 'category[]' && filter.value === 'tilattavat-autot');
      if (!hasOrderableCategory) {
        filters.push({
          name: activeCategoryFilterName,
          text: 'Tilausautot',
          value: 'tilattavat-autot'
        });
      }
    }

    const outgoingFilters = this.#normalizeOutgoingFilters(filters);

    const seen = new Set();
    const deduplicatedFilters = outgoingFilters.filter(filter => {
      const key = `${filter.name}:${filter.value}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (Array.isArray(deduplicatedFilters)) {
      const formattedAjaxBody = formatAjaxBody(deduplicatedFilters, this.#currentBrand);

      const ajaxBody = convertUrlSearchParams(formattedAjaxBody.ajaxBody, false, this.#currentPage);

      // User has changed filters, scroll smoothly to top
      if(options.scrollToTop) {
        window.scroll({
          top: 0,
          behavior: 'smooth'
        });
      }

      if (!options.doNotSaveHistory) {
        // Don't show page index if we are on the page zero.
        this.#updateURL(convertUrlSearchParams(formattedAjaxBody.filteredAjaxBody, this.#currentPage === 0, this.#currentPage + 1), filters, options.loadMore);
      }

      if (updateTags) {
        this.#updateFilterTags(filters, updateTags);
      }

      try {
        await fetch(`/stage-ajax/2022-car-search/?${ajaxBody}`, {
          signal: this.abortController.signal
        })
          .then(response => response.json())
          .then(response => {
            // Show no seo container if no cars found
            if (response.paging.totalResults + response.factoryCarGroups.length <= 0) {
              this.#seoTextContainer.hidden = true;
            }

            this.toggleLoadingAnimation = false;

            this.#elementPartials.carSearchTitle.textContent = response.carSearchTitle || 'Hakutulokset';
            this.#renderData(response.vehicles, response.factoryCarGroups, response.lifts, options.loadMore, response.carSearchSeoText, response.showNoResultsBlurb, response.lowResultCardData);

            // When not loading more, update URL and set load more button invsible
            // if (!options.doNotSaveHistory) {
            //  this.#elementPartials.loadMore.classList.remove('car-search-main__load-more-button--in');
            //}

            // Update results count
            for (const carCountElement of this.#elementPartials.carCountElements) {
              // Sum possible factoryCarGroups length and current GT-X cars
              carCountElement.dataset.carCount = response.paging.totalResults + response.factoryCarGroups.length;
            }

            // Save only GT-X cars count to avoid load nothing when
            // search results ends
            this.#currentCarsCount = response.paging.totalResults;

            // Update page title
            document.title = response.pageTitle;

            // Calculate visible cars count
            this.#visibleCarsCount = this.#elementPartials.carsGrid.querySelectorAll('.card--basic-card').length;
            this.#handleNoMoreResultsLeft();

            // Get first and last cards top and scrollheight
            this.#setFirstAndLastCardsPositionInfo();
          }).catch(() => { });
      } catch (e) {
        console.warn('Search aborted', e);
      }
    }
  }

  /**
   * Manipulate handle modal facet filters according to selectedParams
   *
   * @param {object[]} selectedParams         - Selected parameters
   * @param {object}   options                - Options
   * @param {boolean}  options.freeTextSearch - Defines if we are manipulating from freeTextSearch
   */
  async manipulateSearchParameters(selectedParams, options = {}) {
    const currentSelectedParams = formatSelectedFiltersData(Array.from(this.#inputs));
    this.updateFilterCountElements(currentSelectedParams);

    const filtersToRemove = currentSelectedParams.filter(filter => {
      if (this.#isOrderableTabActive && filter.name === 'category[]' && filter.value === 'tilattavat-autot') {
        return false;
      }
      return !selectedParams.find(selectedParam => selectedParam.name === filter.name && selectedParam.value === filter.value);
    });

    const filtersToAdd = selectedParams.filter(filter =>
      currentSelectedParams.findIndex(selectedParam => selectedParam.name === filter.name && selectedParam.value === filter.value) === -1
    );

    filtersToRemove.forEach(async filter => {
      const input = document.querySelector(`input[name="${filter.name}"][value="${filter.value}"], input[type="text"][name="${filter.name}"]`);
      if (input && (input.type === 'checkbox' || input.type === 'radio')) {
        input.checked = false;

        // If parent element is checkbox-range-option, remove checked class
        if (input.parentNode.classList.contains('checkbox-range-option')) {
          input.parentNode.classList.remove('checked');
        }
      }

      // Handle text input removation
      if (input && input.type === 'text') {
        input.value = '';
      }

      if (!input && window.multiselects[filter.name]) {
        const optionToToggle = window.multiselects[filter.name].options.find(o => o.value === filter.value);
        window.multiselects[filter.name].unSelect(optionToToggle);
      }

      // If brands does not exists, run handleModelDropdown to empty and disable models dropdown
      const normalizedFilterName = this.#normalizeOutgoingFilterName(filter.name);
      if (normalizedFilterName === 'brands[]' && !filtersToAdd.some(f => this.#normalizeOutgoingFilterName(f.name) === 'brands[]')) {
        await this.handleModelDropdown();
      }
    })

    filtersToAdd.forEach(async filter => {
      const input = document.querySelector(`input[name="${filter.name}"][value="${filter.value}"], input[type="text"][name="${filter.name}"]`);
      if (input && (input.type === 'checkbox' || input.type === 'radio')) {
        input.checked = true;

        // If parent element is checkbox-range-option, add checked class
        if (input.parentNode.classList.contains('checkbox-range-option')) {
          input.parentNode.classList.add('checked');
        }
      }

      // Handle text inputs last state
      if (input && input.type === 'text') {
        input.value = filter.value;
      }

      // All dropdowns except models
      // Models depends on brands, and models are get ouside of this loop
      const normalizedName = this.#normalizeOutgoingFilterName(filter.name);
      if (!input && window.multiselects[filter.name] && normalizedName !== 'models[]') {
        const optionToToggle = window.multiselects[filter.name].options.find(o => o.value === filter.value);
        window.multiselects[filter.name].selectOption(optionToToggle);
      }
    });

    // If models exists in filtersToAdd, get models from server
    const modelsFilters = filtersToAdd.filter(f => this.#normalizeOutgoingFilterName(f.name) === 'models[]');
    if (modelsFilters.length > 0 && !options.freeTextSearch) {
      await this.handleModelDropdown(false, modelsFilters);
    }

    // Run tertiary change
    Object.keys(window.tertiaries).forEach(tertiary => {
      window.tertiaries[tertiary].inputChange(false);
    });


    this.#updateFilterTags(selectedParams);

    // If no filters are selected (clear all is probably clicked),
    // check that also tax value inputs and plug type inputs are hidden
    if (selectedParams.length === 0) {
      this.#handleTaxValueVisibility();
      this.#handlePropulsionChange();
    }
  }

  updateFilterCountElements(returnValue) {
    for (const filterCountElement of this.#elementPartials.filterCountElements) {
      filterCountElement.dataset.filterCount = returnValue.length;
    }
  }

  /**
   * Get selected filters from DOM and return
   * FilterTags -friendly object of selected values
   *
   * @returns {object[]}
   */
  getSelectedFilters() {
    const returnValue = Array.from(this.#inputs).flatMap(filter => {
      let returnValue = {};
      // If checkbox or radio, add this to returnValue

      if (isCheckboxOrRadioFilter(filter)) {
        returnValue = handleCheckboxAndRadio(filter);
      }

      // Text inputs, predictive etc.
      if (isTextInput(filter)) {
        returnValue = handleTextInput(filter)
      }

      // Get multiselects
      if (isSelectElement(filter)) {
        returnValue = handleSelect(filter);
      }

      if (Object.keys(returnValue).length && filter.dataset.noTag) {
        returnValue.noTag = true;
      }

      return returnValue.multiple ? returnValue.multiple : returnValue;
    }).filter(o => Object.keys(o).length);

    // Update selected filters count
    for (const filterCountElement of this.#elementPartials.filterCountElements) {
      filterCountElement.dataset.filterCount = returnValue.length;
    }

    return returnValue;
  }

  /**
   * Set loading state, if loading set loader
   * visible and fade search container
   *
   * @param {boolean} value - True if loading, otherwise false
   */
  set toggleLoadingAnimation(value) {
    toggleSearchActive(value);
    this.#isLoading = value;
  }

  /**
   * Render data
   * @param {object[]} nwCars       - Vehicles array from server
   * @param {object[]} factoryCars  - Factory car groups from server
   * @param {object[]} liftCards    - Lift cards from server {title: '', text: '', buttonText: '', buttonUrl: ''
   * @param {boolean} append        - Append vehicles'
   * @param {object}   carSearchSeo - Render car search seo texts to bottom of page
   * @param {object}   showNoResultsBlurb - Render no results blurb
   * @param {object}   lowResultCardData - Data for low result card
   */
  #renderData(nwCars, factoryCars, liftCards, append = false, carSearchSeo = {}, showNoResultsBlurb = null, lowResultCardData = {}) {

    const taxValueParams = [
      'limited_benefit_min',
      'limited_benefit_max',
      'unlimited_benefit_min',
      'unlimited_benefit_max'
    ];

    const selectedFilters = formatSelectedFiltersData(Array.from(this.#inputs));
    this.updateFilterCountElements(selectedFilters);

    const showTaxValueCard = selectedFilters.some(
      filter => taxValueParams.includes(filter.name) && filter.value !== undefined && filter.value !== null && filter.value !== ''
    );

    // Show either tax value card or basic card depending on selected filters
    const carsMarkup = nwCars.map(car => {
      if (showTaxValueCard && this.#cards.taxValueCard) {
        return window.Dali.render(this.#cards.taxValueCard.template, {
          ...car,
          cardCustomClasses: this.#cards.taxValueCard.cardCustomClasses
        }, this.#cards.taxValueCard.partials);
      } else {
        const customClasses = `${this.#cards.basicCard.cardCustomClasses}${car.labels.length ? ' card--label' : ''}`;
        return window.Dali.render(this.#cards.basicCard.template, {
          ...car,
          cardCustomClasses: customClasses
        }, this.#cards.basicCard.partials);
      }
    });

    const showHedinDriveModelCard = selectedFilters.some(
      filter => this.#normalizeOutgoingFilterName(filter.name) === 'hedin_drive' && filter.value === '1'
    );

    const factoryCarsMarkup = factoryCars.map(car => {
      const card = showHedinDriveModelCard && this.#cards.hedinDriveModelCard
        ? this.#cards.hedinDriveModelCard
        : this.#cards.modelCard;
      return window.Dali.render(card.template, {
        ...car,
        cardCustomClasses: card.cardCustomClasses
      }, card.partials);
    });

    // Show lifts only if there are any cars found
    if (carsMarkup.length || factoryCarsMarkup.length) {
      liftCards.forEach((lift, index) => {
        const liftMarkup = window.Dali.render(this.#cards.liftCard.template, {
          ...lift,
          cardCustomClasses: this.#cards.liftCard.cardCustomClasses
        }, this.#cards.liftCard.partials);

        const indexToInject = (index + 1) * 3; // 3, 6, 9 etc.

        if (factoryCarsMarkup.length) {
          factoryCarsMarkup.splice(indexToInject, 0, liftMarkup);
        } else {
          carsMarkup.splice(indexToInject, 0, liftMarkup);
        }
      })
    }

    if (showNoResultsBlurb) {
      this.#elementPartials.blurbContainer.innerHTML = window.Dali.render(this.#blurbTemplate, {...showNoResultsBlurb}, {contentPartial: this.#contentPartial});
    } else {
      this.#elementPartials.blurbContainer.innerHTML = '';
    }

    if (lowResultCardData) {
      const lowResultMarkup = window.Dali.render(this.#cards.liftCard.template, {
        ...lowResultCardData,
        cardCustomClasses: this.#cards.liftCard.cardCustomClasses
      });

      carsMarkup.unshift(lowResultMarkup);
    }

    const grid = document.querySelector('.car-search-grid');

    if (!append) {
      grid.innerHTML = factoryCarsMarkup.join('') + carsMarkup.join('');
      if(Object.keys(carSearchSeo).length) {
        this.#elementPartials.carSearchSeo.innerHTML = window.Dali.render(this.#carSearchSeoTemplate, {seoText: carSearchSeo});
        this.#elementPartials.carSearchSeo.classList.remove('hidden');
      } else {
        this.#elementPartials.carSearchSeo.classList.add('hidden');
        this.#elementPartials.carSearchSeo.innerHTML = '';
      }
    } else {
      grid.insertAdjacentHTML('beforeend', carsMarkup.join(''));
    }

    // Replace searchWatcherButton link with new search parameters
    if (this.#elementPartials.searchWatcherButton) {
      const params = formatAjaxBody(selectedFilters, this.#currentBrand).filteredAjaxBody;
      const urlParams = convertUrlSearchParams(params, false, this.#currentPage);
      this.#elementPartials.searchWatcherButton.href = `${window.searchWatcherLink}?${urlParams}`;
    }
  }

  /**
   * Aborts current search if search is running
   */
  #abortSearch() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Resolve multiselect key by active main tab.
   * Orderable tab uses `--orderable` suffix in field names/ids.
   *
   * @param {string} baseName base multiselect name/id
   * @returns {string}
   */
  #getMainTabFilterName(baseName) {
    const orderableName = `${baseName}${ORDERABLE_FILTER_SUFFIX}`;

    if (this.#isOrderableTabActive) {
      return orderableName;
    }

    return baseName;
  }

  /**
   * Strip the orderable suffix from filter names before serializing request and URL params.
   *
   * @param {string} filterName Filter name to normalize
   * @returns {string}
   */
  #normalizeOutgoingFilterName(filterName) {
    return filterName.endsWith(ORDERABLE_FILTER_SUFFIX)
      ? filterName.slice(0, -ORDERABLE_FILTER_SUFFIX.length)
      : filterName;
  }

  /**
   * Normalize outgoing filters without mutating UI-state filters used elsewhere.
   *
   * @param {object[]} filters Filters to normalize for outgoing serialization
   * @returns {object[]}
   */
  #normalizeOutgoingFilters(filters = []) {
    return filters.map(filter => ({
      ...filter,
      name: this.#normalizeOutgoingFilterName(filter.name)
    }));
  }

  /**
   * Bind events to necessary inputs
   *
   * @private
   */
  #bindEvents() {
    // Listen tertiary event
    this.#elementPartials.changeOrder.addEventListener('tertiaryChange', e => {
      this.searchCars();
    });

    Object.values(MAIN_FILTER_TAB_IDS).forEach((tabId) => {
      const tabButton = this.#handleModal.querySelector(`[aria-controls="${tabId}"]`);

      tabButton?.addEventListener('click', async () => {
        this.#lastBrandToSearch = [];

        const activeMainTab = this.#handleModal?.querySelector(`#${tabId}`);
        const tabInputs = activeMainTab?.querySelectorAll('.car-search-effective, .handle-modal select');
        if (tabInputs) {
          const orderInputs = document.querySelectorAll('#order-select-container .car-search-effective');
          this.#inputs = [...tabInputs, ...orderInputs];
        }

        const hedinDriveFilter = this.getSelectedFilters().find(f =>
          f.name === 'hedin_drive' || f.name === `hedin_drive${ORDERABLE_FILTER_SUFFIX}`
        );
        const targetHedinDriveName = tabId === MAIN_FILTER_TAB_IDS.orderable
          ? `hedin_drive${ORDERABLE_FILTER_SUFFIX}`
          : 'hedin_drive';

        let categories = [];
        if (tabId === MAIN_FILTER_TAB_IDS.orderable) {
          this.#isOrderableTabActive = true;
          this.#elementPartials.searchWatcherButton?.classList.add('hidden');
          categories = [{ name: 'category[]', text: 'Tilausautot', value: 'tilattavat-autot' }];
        } else {
          this.#isOrderableTabActive = false;
          this.#elementPartials.searchWatcherButton?.classList.remove('hidden');
          categories = [
            { name: 'category[]', text: 'Esittelyautot', value: 'esittelyautot' },
            { name: 'category[]', text: 'Vaihtoautot', value: 'vaihtoautot' }
          ];
        }

        if (hedinDriveFilter) {
          categories.push({ ...hedinDriveFilter, name: targetHedinDriveName });
        }

        await this.manipulateSearchParameters(categories);

        if (hedinDriveFilter) {
          this.#handleModal.querySelectorAll(`input[name="${targetHedinDriveName}"]`).forEach(input => {
            input.checked = true;
          });
        }

        this.#handlePropulsionChange();

        const modelsSuffix = tabId === MAIN_FILTER_TAB_IDS.orderable ? ORDERABLE_FILTER_SUFFIX : '';
        const modelsDropdown = window.multiselects[`models[]${modelsSuffix}`];
        if (modelsDropdown) {
          modelsDropdown.unSelectAll(false);
          modelsDropdown.disableMultiselect();
          modelsDropdown.removeOptions(modelsDropdown.options.map(o => o.value), false);
        }

        this.searchCars(true, { loadMore: false, scrollToTop: true, doNotSaveHistory: false });
      });
    });


    // Load more event
    this.#elementPartials.loadMore.addEventListener('click', this.#loadMore.bind(this));

    this.#handleModal.querySelectorAll('select[name="fuel[]"], select[name="fuel[]--orderable"]').forEach(select => {
      select.addEventListener('change', (event) => {
        this.#handlePropulsionChange(event);
      });
    });

    this.#handleModal.querySelector('input#uudet-autot').addEventListener('change', (event) => {
      this.#handleTaxValueVisibility(event);
    });

    // Get sidebar input list and add events
    this.#handleModal.querySelectorAll('[type="radio"], [type="checkbox"], select').forEach(element => {
      element.addEventListener('change', () => {
        if (this.#normalizeOutgoingFilterName(element.name) === 'hedin_drive') {
          const parentTabContent = element.closest('.tabs__content');
          const tabTextElement = parentTabContent?.querySelector('.js-monthly-price-tab');
          const tabButton = tabTextElement?.closest('.tabs__tab');
          const defaultHeading = tabButton?.dataset.defaultHeading;
          const altHeading = tabButton?.dataset.altHeading;

          // Get monthly price inputs
          const monthlyPriceInputs = Array.from(this.#inputs).filter((input) =>
            ['monthly_price_min', 'monthly_price_max'].includes(this.#normalizeOutgoingFilterName(input.name))
          );
          const hedinDriveMonthlyPriceInputs = Array.from(this.#inputs).filter((input) =>
            ['hd_monthly_price_min', 'hd_monthly_price_max'].includes(this.#normalizeOutgoingFilterName(input.name))
          );

          monthlyPriceInputs.forEach(input => {
            input.closest('.predicting-input-field').hidden = element.checked;

            if (element.checked) {
              input.value = '';
            }
          });

          hedinDriveMonthlyPriceInputs.forEach(input => {
            input.closest('.predicting-input-field').hidden = !element.checked;

            if (!element.checked) {
              input.value = '';
            }
          });

          if (element.checked) {
            tabTextElement.textContent = altHeading;
          } else {
            tabTextElement.textContent = defaultHeading;
          }
        }

        this.searchCars(true, { loadMore: false, scrollToTop: true, doNotSaveHistory: false });
      });
    });

    this.#handleModal.querySelectorAll('.predicting-input-field [type="text"]').forEach(element => {
      element.addEventListener('predictingInputChange', (event) => {
        if (event.target.closest('#car-search-filters-prices, #car-search-filters-prices--orderable')) {
          this.#handlePriceInputChanges(event.target);
        }
        if (event.target.closest('#car-search-filters-tax-values')) {
          this.#handleTaxValueInputChanges(event.target);
        }
        this.searchCars();
      })
    })

    window.addEventListener('popstate', this.#handlePopState.bind(this));

    document.addEventListener('tagListChange', async (e) => {
      await this.manipulateSearchParameters(e.detail.filters);
      document.dispatchEvent(new CustomEvent('searchParametersManipulated'));
      this.searchCars();
    });

    this.#elementPartials.carsGrid.addEventListener('click', this.#carCardClickEvent.bind(this));

    // Handle modal display status
    document.addEventListener('handleModalToggle', e => {
      if (!e.detail.isOpen) {
        pushFiltersToDataLayer('UI - Car Search - Facets closed', formatSelectedFiltersData(Array.from(this.#inputs)));
      } else {
        analytics.pushToDataLayer({
          event: 'UI Interaction',
          interaction: {
            element: '',
            elementName: 'UI - Car Search - Facets opened',
            type: 'click'
          }
        })
      }
    });

    // Scroll & resize events
    ['scroll', 'resize'].forEach(eventListenerType => window.addEventListener(eventListenerType, () =>
      this.#calculateLoadingSpinnerPosition(eventListenerType === 'resize'),
    { passive: true })
    );
  }

  /**
   * Called on propulsion value change.
   */
  #handlePropulsionChange() {
    const activeMainTabPanel = this.#handleModal.querySelector('.tabs__content.tab--open');
    const activeEvFilterGroup = activeMainTabPanel?.querySelector('.ev-group');

    const suffix = this.#getMainTabFilterName('fuel[]').endsWith(ORDERABLE_FILTER_SUFFIX)
      ? ORDERABLE_FILTER_SUFFIX
      : '';
    const fuelMultiselect = window.multiselects[`fuel[]${suffix}`];
    const batteryCapacityMultiselect = window.multiselects[`battery_capacity_min${suffix}`] || window.multiselects.battery_capacity_min;
    const chargingCapacityMultiselect = window.multiselects[`charging_capacity_min${suffix}`] || window.multiselects.charging_capacity_min;
    const electricalRangeMultiselect = window.multiselects[`electrical_range_min${suffix}`] || window.multiselects.electrical_range_min;
    const chademoElement = this.element.querySelector(`#CHAdeMO${suffix}`)?.closest('.radio-element');
    const plugTypeInputs = this.element.querySelectorAll(`input[name="plug_type${suffix}"]`);

    if (!fuelMultiselect) {
      return;
    }

    const isElectricalPropulsionChosen = fuelMultiselect.value.some((fuel) => ['S','P'].includes(fuel)) &&
                                         fuelMultiselect.value.length === 1;
    activeEvFilterGroup?.classList.toggle('hidden', !isElectricalPropulsionChosen);

    // These filters are going to be hidden if propulsion is not electrical or plug-in hybrid.
    // So for that reason we need to unselect all values from these filters.
    if (!isElectricalPropulsionChosen) {
      plugTypeInputs.forEach(input => input.checked = false);
      batteryCapacityMultiselect?.unSelectAll(false);
      chargingCapacityMultiselect?.unSelectAll(false);
      electricalRangeMultiselect?.unSelectAll(false);
    }

    // Filters are hidden and shown depending on the propulsion type is electrical or plug in hybrid.
    if( isElectricalPropulsionChosen ) {
      const isPlugInHybrid = fuelMultiselect.value.includes('P');

      batteryCapacityMultiselect?.element.closest('.car-search-filters').classList.toggle('hidden', isPlugInHybrid);
      chargingCapacityMultiselect?.element.closest('.car-search-filters').classList.toggle('hidden', isPlugInHybrid);
      batteryCapacityMultiselect?.show(!isPlugInHybrid);
      chargingCapacityMultiselect?.show(!isPlugInHybrid);
      chademoElement?.classList.toggle('hidden', isPlugInHybrid);

      if( isPlugInHybrid ) {
        batteryCapacityMultiselect?.unSelectAll(false);
        chargingCapacityMultiselect?.unSelectAll(false);
        const chademoInput = chademoElement?.querySelector('input');
        if (chademoInput) {
          chademoInput.checked = false;
        }
      }
    }
  }

  /**
   * Calculate spinner position in viewport
   *
   * @param {boolean} forceCalculate - Ignore "isLoading" and "lastLoaderState"
   * @private
   */
  #calculateLoadingSpinnerPosition(forceCalculate = false) {
    if (this.#isLoading || forceCalculate) {
      const firstCardOverHalf = elementIsCenterOfScreen(this.#firstAndLastCardElements.first);
      const lastCardOverHalf = elementIsCenterOfScreen(this.#firstAndLastCardElements.last);

      // First card and last card has not crossed center of screen --> top
      if (!firstCardOverHalf && !lastCardOverHalf && (this.#lastLoaderState !== 'top' || forceCalculate)) {
        this.#lastLoaderState = 'top';
        this.#elementPartials.loadMoreSpinner.classList.remove('car-search-main__loader-holder--fixed');
        // Get offset top value from
        this.#setLoaderInitialTopValue();
      }

      // First card has crossed center of screen --> scrolling
      if (firstCardOverHalf && !lastCardOverHalf && (this.#lastLoaderState !== 'fixed' || forceCalculate)) {
        this.#lastLoaderState = 'fixed';
        this.#elementPartials.loadMoreSpinner.style.top = '';
        this.#elementPartials.loadMoreSpinner.classList.add('car-search-main__loader-holder--fixed');
      }

      // Last card has crossed center of screen --> bottom
      if (firstCardOverHalf && lastCardOverHalf && (this.#lastLoaderState !== 'bottom' || forceCalculate)) {
        this.#lastLoaderState = 'bottom';
        this.#elementPartials.loadMoreSpinner.classList.remove('car-search-main__loader-holder--fixed');
        this.#elementPartials.loadMoreSpinner.style.top = `${this.#firstAndLastCardElements.last.offsetTop +
          (this.#firstAndLastCardElements.last.scrollHeight / 2) -
          (this.#elementPartials.loadMoreSpinner.scrollHeight / 2)}px`;
      }
    }
  }

  /**
   * Handles results injecting to cars grid if hash #|_s and sessionStorage exists
   *
   * @private
   */
  #handleResultsFromSessionStorage() {
    const sessionStorageData = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    const lastPagingStorageData = window.sessionStorage.getItem(LAST_PAGING_SESSION_STORAGE_KEY);

    // When coming back from car page (back button pressed in browser)
    // URL contains this #|_s (LS = LocalStorage) hash.
    // When that hash exists, we will load car search results from
    // local storage straight to the dom. User won't need to
    // scroll down again
    if (sessionStorageData && window.location.hash === '#|_s') {
      this.#sessionStorageLoaded = true;
      this.#elementPartials.carsGrid.innerHTML = sessionStorageData;
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      history.replaceState(null, '', ' '); // Remove #|_s completely

      if(lastPagingStorageData) {
        this.#currentPage = +lastPagingStorageData;
        window.sessionStorage.removeItem(LAST_PAGING_SESSION_STORAGE_KEY);
      }
    }

    // Show load more button if current page is over
    // maximum pages to load with infinite scroll
    if (this.#currentPage >= this.#maxInfiniteLoadPages) {
      this.#elementPartials.loadMore.classList.add('car-search-main__load-more-button--in');
    }
  }

  /**
   * Car card click event stores current search results to sessionStorage
   * If user presses back button, load items from sessionStorage. Also
   * handles pushing to dataLayer.
   *
   * @param {Event} event - Click event
   * @returns {boolean} return false is modal is shown
   */
  #carCardClickEvent(event) {

    if(ModalForm.isSupportedModal(event?.target?.dataset?.target)) {
      ModalForm.showModal(event?.target?.dataset?.target);
      event.preventDefault();
      return false;
    }

    event.preventDefault();
    const linkElement = event.target.closest('a.card');
    if (linkElement) {
      analytics.pushToDataLayer({
        event: 'UI Interaction',
        interaction: {
          elementName: 'UI - Car Search - Car Card Click',
          selectedFilters: formatSelectedFiltersToDataLayer(formatSelectedFiltersData(Array.from(this.#inputs))),
          type: 'click'
        }
      });

      // Try adding data to sessionStorage
      try {

        // Add current results to sessionStorage
        window.sessionStorage.setItem(
          SESSION_STORAGE_KEY,
          this.#elementPartials.carsGrid.innerHTML
        );

        // Add current paging to sessionStorage as an own item
        window.sessionStorage.setItem(
          LAST_PAGING_SESSION_STORAGE_KEY,
          this.#currentPage
        );
      } catch(e) {
        // Clear sessionStorage
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
        window.sessionStorage.removeItem(LAST_PAGING_SESSION_STORAGE_KEY);
        console.warn('Could not add data to sessionStorage. Quota might has been exceeded... Skipping sessionStorage.', e);
      }

      window.location.hash = '#|_s';
      window.location.href = linkElement.href;
    }
  }

  /**
   * Handles model dropdown disabled -states and
   * show or hide optgroups depends on what value
   * brands[] dropdown has.
   * @param {boolean} forceModelSearch Force model search, default false
   * @param {string[]} lastStateModels Last state models, default []
   */
  async handleModelDropdown(forceModelSearch = false, lastStateModels = []) {
    // If brands[] dropdown value is empty, unselect and disable
    const brandsDropdown = window.multiselects[this.#getMainTabFilterName('brands[]')];
    const modelsDropdown = window.multiselects[this.#getMainTabFilterName('models[]')];

    if (!brandsDropdown || !modelsDropdown) {
      return;
    }

    if (!brandsDropdown.getValue().length) {
      modelsDropdown.unSelectAll(false);
      modelsDropdown.disableMultiselect();
      modelsDropdown.removeOptions(modelsDropdown.options.map(o => o.value), false);
    }

    if (brandsDropdown.getValue().length) {
      const brandsValues = brandsDropdown.getValue();

      if (!compareArrays(brandsValues,this.#lastBrandToSearch) || forceModelSearch) {

        const models = await this.#getModelsFromServer(brandsValues);

        if (this.#lastBrandToSearch || forceModelSearch) {
          modelsDropdown.unSelectAll(false);
          modelsDropdown.removeOptions(modelsDropdown.options.map(o => o.value), false);
        }

        const modelsData = models.map(model => ({
          value: model.slug,
          shownValue: model.name,
          selected: Boolean(lastStateModels.find(m => m === model.slug))
        }));

        modelsDropdown.addOptions(modelsData);
      } else {
        if (lastStateModels.length) {
          lastStateModels.forEach(model => modelsDropdown.selectOption({ value: model }));
        }
      }

      modelsDropdown.enableMultiselect();
    }
  }

  /**
   * Getter for isOrderableTabActive. Used e.g. in Car Search Free Text to determine which filters to show.
   *
   * @return {boolean}
   */
  get isOrderableTabActive() {
    return this.#isOrderableTabActive;
  }

  /**
   * If URL contains category[]=tilattavat-autot on page load, set orderable tab as active
   */
  #initOrderableTabFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasOrderableCategory = urlParams.getAll('category[]').includes('tilattavat-autot');

    if (hasOrderableCategory) {
      this.#isOrderableTabActive = true;
    }
  }

  /**
   * Get brand models from server
   *
   * @param {string[]} brandSlugs - Brand slug
   * @returns {object[]}
   */
  async #getModelsFromServer(brandSlugs) {
    const searchParameters = new URLSearchParams();

    brandSlugs.forEach( slug => {
      searchParameters.append('brandSlug[]', slug);
    });

    return await fetch(`/stage-ajax/getModelsByBrand/?${searchParameters.toString()}`)
      .then(response => response.json());
  }

  /**
   * Updates URL
   *
   * @param {string} searchParamsString  - Same as sent to ajax
   * @param {object[]} selectedFilters - FIlters which is passed to FilterTags
   * @param {boolean} loadMore - If load more then don't use pushState but replaceState instead.
   */
  #updateURL(searchParamsString, selectedFilters, loadMore) {
    // Generate URL via
    const searchParametersUrl = `${window.baseUrl}?${searchParamsString.toString()}`;

    if(loadMore ) {
      history.replaceState(selectedFilters, '', searchParametersUrl);
    } else {
      history.pushState(selectedFilters, '', searchParametersUrl);
    }
  }

  /**
   * Handle popstate when user hits browser's back or forward button
   *
   * @param {Event} event Popstate event
   */
  async #handlePopState(event) {
    // Check that URL does not contain hash
    if (window.location.hash === '' && !this.#sessionStorageLoaded && Object.keys(event.state).length) {
      this.toggleLoadingAnimation = true;
      await this.manipulateSearchParameters(event.state);

      // Set sessionStorageLoaded to its initial value
      this.#sessionStorageLoaded = false;
      this.searchCars(false, { doNotSaveHistory: true, scrollToTop: true, doNotHandleModelDropdown: true });
    }
  }

  #handleNoMoreResultsLeft() {
    if (this.#currentCarsCount > 0 && this.#currentCarsCount <= this.#visibleCarsCount) {
      this.#elementPartials.noMoreResults.classList.add('no-more-results--in');

      // Hide load more if exists
      this.#elementPartials.loadMore.classList.remove('car-search-main__load-more-button--in');
    } else {
      this.#elementPartials.noMoreResults.classList.remove('no-more-results--in');
    }
  }

  /**
   * Updates FilterTags. Removes filters which have noTag property and duplicate filters before passing to FilterTags.
   *
   * @param {object[]} filters - Filters objects
   * @see FilterTags
   */
  #updateFilterTags(filters) {
    if (FilterTags.instance) {
      const filtersWithoutNoTag = filters.filter(f => !f.noTag);
      const seenFilters = new Set();
      const uniqueFilters = filtersWithoutNoTag.filter((filter) => {
        const normalizedName = this.#normalizeOutgoingFilterName(filter.name || '');
        const key = `${normalizedName}:${filter.value || ''}`;

        if (seenFilters.has(key)) {
          return false;
        }

        seenFilters.add(key);
        return true;
      });

      FilterTags.instance.updateFilterTags(uniqueFilters);
    }
  }

  /**
   * Load more button click event
   *
   * @param {Event} event - Binded event
   */
  #loadMore(event) {
    event.preventDefault();
    if (this.#currentPage === this.#maxInfiniteLoadPages) {
      this.#elementPartials.loadMore.classList.add('car-search-main__load-more-button--in');
    }
    this.#currentPage += 1;
    this.searchCars(true, { loadMore: true, doNotSaveHistory: false });
  }

  /**
   * Observer for load more button when user
   * scrolls button to viewport, execute load more
   */
  #observeLoadMoreButton() {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (
          entry.isIntersecting &&
          !this.#isLoading &&
          this.#currentPage <= this.#maxInfiniteLoadPages &&
          this.#currentCarsCount > this.#visibleCarsCount
        ) {
          this.#elementPartials.loadMore.click();
        }
      });
    }, { threshold: 1 });

    observer.observe(this.#elementPartials.loadMore);
  }

  /**
   * Handle price input changes.
   * Empty price inputs on monthly price input changes and vice versa.
   * @param {EventTarget} changedElement - Changed element
   */
  #handlePriceInputChanges(changedElement) {
    const changedName = this.#normalizeOutgoingFilterName(changedElement.name);

    const priceInputs = {
      price_min: this.#handleModal.querySelector(`input[name="${this.#getMainTabFilterName('price_min')}"]`),
      price_max: this.#handleModal.querySelector(`input[name="${this.#getMainTabFilterName('price_max')}"]`),
      monthly_price_min: this.#handleModal.querySelector(`input[name="${this.#getMainTabFilterName('monthly_price_min')}"]`),
      monthly_price_max: this.#handleModal.querySelector(`input[name="${this.#getMainTabFilterName('monthly_price_max')}"]`),
      hd_monthly_price_min: this.#handleModal.querySelector(`input[name="${this.#getMainTabFilterName('hd_monthly_price_min')}"]`),
      hd_monthly_price_max: this.#handleModal.querySelector(`input[name="${this.#getMainTabFilterName('hd_monthly_price_max')}"]`),
    };

    if (changedName === 'price_min' || changedName === 'price_max') {
      if (changedElement.value && priceInputs.monthly_price_min) {
        priceInputs.monthly_price_min.value = '';
        priceInputs.monthly_price_max.value = '';
      }
      if (changedElement.value && priceInputs.hd_monthly_price_min) {
        priceInputs.hd_monthly_price_min.value = '';
        priceInputs.hd_monthly_price_max.value = '';
      }
    } else if ( changedName.includes('monthly_price_min') || changedName.includes('monthly_price_max')) {
      // Empty price fields on monthly price input changes
      priceInputs.price_min.value = '';
      priceInputs.price_max.value = '';
    }
  }

  /**
   * Handle tax value input changes.
   * Empty limited benefit value on unlimited benefit input changes and vice versa.
   * @param {EventTarget} changedElement - Changed element
   */
  #handleTaxValueInputChanges(changedElement) {
    if ((changedElement.name.includes('limited_benefit_min') || changedElement.name.includes('limited_benefit_max')) && changedElement.value) {
      // Empty unlimited benefit fields on limited benefit input changes
      this.#taxValueInputs.unlimited_benefit_min.value = '';
      this.#taxValueInputs.unlimited_benefit_max.value = '';
    } else if ((changedElement.name.includes('unlimited_benefit_min') || changedElement.name.includes('unlimited_benefit_max')) && changedElement.value) {
      // Empty limited benefit fields on unlimited benefit input changes
      this.#taxValueInputs.limited_benefit_min.value = '';
      this.#taxValueInputs.limited_benefit_max.value = '';
    }
  }

  /**
   * Called on new cars checkbox change.
   * Hides the accordion and unselects all options if the new cars checkbox is unchecked.
   */
  #handleTaxValueVisibility() {
    const taxValuesDiv = this.element.querySelector('#car-search-filters-tax-values');
    if (!taxValuesDiv) return;

    const accordionParent = taxValuesDiv.closest('.hedin-accordion');
    if (!accordionParent) return;

    const newCarsCheckbox = this.element.querySelector('#uudet-autot');
    const isNewCarChecked = newCarsCheckbox && newCarsCheckbox.checked;

    if (!isNewCarChecked) {
      accordionParent.classList.add('hidden');

      // Empty all the fields of benefitInputs
      Object.values(this.#taxValueInputs).forEach(input => {
        if (input) input.value = '';
      });

    } else {
      // Show the accordion parent if new car is checked
      accordionParent.classList.remove('hidden');
    }
  }

  /**
   * Set loader initial top value
   *  - Get first card on dom
   *  - Get bounding client rect top value
   *  - Get car scrollHeight / 2
   *  - Set loader top value: bouningClientRect.top + scrollHeight / 2
   */
  #setLoaderInitialTopValue() {
    const firstCard = this.#firstAndLastCardElements.first;
    this.#elementPartials.loadMoreSpinner.style.top = `${(firstCard.scrollHeight / 2) + firstCard.offsetTop - (this.#elementPartials.loadMoreSpinner.scrollHeight / 2)}px`;
  }

  /**
   * Set first and last car cards top- and scrollHeight -value
   *
   * @private
   */
  #setFirstAndLastCardsPositionInfo() {
    const [firstCard, lastCard] = this.#elementPartials.carsGrid.querySelectorAll('.card:first-child, .card:last-child');

    // If first or last card does not exists, fallback to carsGrid
    // to avoid javascript error when calculating spinner position
    if (!firstCard || !lastCard) {
      this.#firstAndLastCardElements.first = this.#elementPartials.carsGrid;
      this.#firstAndLastCardElements.last = this.#elementPartials.carsGrid;
    } else {
      this.#firstAndLastCardElements.first = firstCard;
      this.#firstAndLastCardElements.last = lastCard;
    }
  }

  /**
   * Changes button visible after scrolling
   */
  #changeButtonVisibleAfterScroll() {
    const filterButton = document.querySelector('.handle-modal__show-filters');

    window.addEventListener('scroll', () => {
      if (document.documentElement.scrollTop > 1000) {
        filterButton.classList.add('visible');
      } else {
        filterButton.classList.remove('visible');
      }
    })
  }

  /**
   * init Static initializer of the class.
   *
   * @static
   */
  static init() {
    const carSearch = document.querySelector('.car-search-2022');

    if (carSearch) {
      new CarSearch(carSearch);
    }
  }
}

export default CarSearch;

export {
  SESSION_STORAGE_KEY,
  LAST_PAGING_SESSION_STORAGE_KEY
}
